/**
 * Smart catalog (Catalog 20 #8): brand / category for items that have none, learned from the part-number
 * prefixes of the items that do (e.g. 4 of 4 items starting "4702" are BMS · Pipe Sensors), and a lead time
 * from purchase-order history. Worked out on the server with the catalog's own data — no outside service.
 * The callout says how many items can be completed; the review dialog accepts all or row by row. Accepting
 * only fills fields that are still empty (the server checks again), as one undo step.
 */
import { useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getErrorMessage } from '@/lib/errors';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { InvCheckbox } from './InventoryUi';
import { plural } from './inventoryModel';

const FIELD_LABEL = { brand: 'Brand', category: 'Category', lead_time_days: 'Lead time' };

/** "LCS · LCP?" / "Brand: BMS?" — the short chip under a catalog row with a suggestion. */
export function suggestionChipText(suggest = {}) {
  if (suggest.brand && suggest.category) return `${suggest.brand} · ${suggest.category}?`;
  if (suggest.category) return `Category: ${suggest.category}?`;
  if (suggest.brand) return `Brand: ${suggest.brand}?`;
  if (suggest.lead_time_days) return `Lead time ${suggest.lead_time_days} d?`;
  return '';
}

/** Up to three "4702… → BMS · Pipe Sensors" examples taken from the reasons. */
function examplesOf(items = []) {
  const seen = new Map();
  items.forEach((item) => {
    const reason = item.reasons?.category || item.reasons?.brand || '';
    const match = reason.match(/starting (\S+?)…/);
    if (!match || seen.has(match[1])) return;
    seen.set(match[1], [item.suggest.brand, item.suggest.category].filter(Boolean).join(' · '));
  });
  return [...seen.entries()].slice(0, 3);
}

export function SuggestionChip({ suggestion, onClick }) {
  const text = suggestionChipText(suggestion?.suggest);
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={(event) => { event.stopPropagation(); onClick?.(); }}
      className="ml-1.5 inline-flex h-[18px] max-w-[180px] items-center gap-1 truncate rounded-full bg-status-violet-bg px-1.5 align-middle text-[11px] font-bold text-status-violet-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      title={Object.values(suggestion.reasons || {}).join(' · ')}
      data-testid={`catalog-suggestion-${suggestion.id}`}
    >
      <Sparkles className="h-[11px] w-[11px] shrink-0" aria-hidden="true" />
      <span className="truncate">{text}</span>
    </button>
  );
}

export function SuggestionsCallout({ suggestions, canEdit, onReview }) {
  const count = suggestions?.count || 0;
  if (!count) return null;
  const examples = examplesOf(suggestions.items);
  const fields = suggestions.fields || {};
  const what = ['brand', 'category', 'lead_time_days'].filter((field) => fields[field]).map((field) => FIELD_LABEL[field].toLowerCase());
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-3 rounded-xl border border-[color:var(--navy-100)] bg-[color:var(--navy-50)] px-3.5 py-3 text-[13px] text-ink" data-testid="catalog-suggestions-callout">
      <span className="ui-icon-tile tone-violet h-9 w-9 bg-surface" aria-hidden="true"><Sparkles className="h-[17px] w-[17px]" /></span>
      <div className="min-w-0 flex-1">
        <p>
          <b className="font-bold">{plural(count, 'item')} can be completed automatically</b>
          <span className="text-ink-3"> · {what.join(' & ') || 'missing details'}</span>
        </p>
        <p className="mt-0.5 truncate text-[12px] text-ink-2">
          Learned from the catalog’s own part-number prefixes{examples.length ? ': ' : '.'}
          {examples.map(([prefix, value], index) => (
            <span key={prefix}>{index ? ', ' : ''}<span className="font-mono text-[11.5px] text-ink">{prefix}…</span> → {value}</span>
          ))}
        </p>
      </div>
      {canEdit ? (
        <Button type="button" size="sm" variant="outline" className="text-ink" onClick={onReview} data-testid="catalog-review-suggestions">
          <Sparkles aria-hidden="true" /> Review suggestions
        </Button>
      ) : null}
    </div>
  );
}

export function SuggestionsDialog({ open, onOpenChange, suggestions, focusId = null, onApply }) {
  const items = useMemo(() => suggestions?.items || [], [suggestions]);
  const [checked, setChecked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setChecked(new Set(focusId ? [focusId] : items.map((item) => item.id)));
    setError('');
    // Reset when the dialog opens (or opens on another row).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusId]);

  const toggle = (id, value) => setChecked((current) => {
    const next = new Set(current);
    if (value) next.add(id); else next.delete(id);
    return next;
  });
  const all = items.length > 0 && checked.size === items.length;

  const apply = async (rows) => {
    if (!rows.length) return;
    setBusy(true);
    setError('');
    try {
      await onApply(rows.map((item) => ({ id: item.id, ...item.suggest })));
      onOpenChange(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the suggestions'));
    } finally {
      setBusy(false);
    }
  };

  const ordered = focusId ? [...items].sort((left, right) => (right.id === focusId) - (left.id === focusId)) : items;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="catalog-suggestions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-lg text-ink"><Sparkles className="h-5 w-5 text-status-violet-ink" aria-hidden="true" /> Complete the catalog</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">
            Suggestions fill <b className="font-semibold text-ink">empty</b> fields only — nothing anyone typed is overwritten. Nothing changes until you accept.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[52vh] overflow-auto rounded-xl border border-line">
          <table className="w-full min-w-[720px] border-separate border-spacing-0 text-[13px]">
            <thead>
              <tr className="text-[11.5px] font-bold text-ink-3">
                <th className="sticky top-0 z-[1] h-9 w-10 border-b border-line bg-surface-2 px-3 text-left">
                  <InvCheckbox checked={all} indeterminate={checked.size > 0 && !all} onCheckedChange={() => setChecked(all ? new Set() : new Set(items.map((item) => item.id)))} aria-label="Select every suggestion" />
                </th>
                <th className="sticky top-0 z-[1] h-9 border-b border-line bg-surface-2 px-3 text-left">Item</th>
                <th className="sticky top-0 z-[1] h-9 border-b border-line bg-surface-2 px-3 text-left">Brand</th>
                <th className="sticky top-0 z-[1] h-9 border-b border-line bg-surface-2 px-3 text-left">Category</th>
                <th className="sticky top-0 z-[1] h-9 border-b border-line bg-surface-2 px-3 text-right">Lead time</th>
                <th className="sticky top-0 z-[1] h-9 border-b border-line bg-surface-2 px-3 text-left">Why</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((item) => {
                const on = checked.has(item.id);
                const cell = (field, current) => (item.suggest[field]
                  ? <span className="inline-flex items-center gap-1 rounded-md bg-status-violet-bg px-1.5 py-0.5 text-[12px] font-semibold text-status-violet-ink">{item.suggest[field]}</span>
                  : <span className="text-ink-3">{current || '—'}</span>);
                return (
                  <tr key={item.id} className={cn(on ? '[&>td]:bg-brand-soft' : '', item.id === focusId && '[&>td:first-child]:shadow-[inset_3px_0_0_var(--brand)]')} data-testid={`suggestion-row-${item.id}`}>
                    <td className="border-b border-line-2 px-3 py-2"><InvCheckbox checked={on} onCheckedChange={(value) => toggle(item.id, Boolean(value))} aria-label={`Accept the suggestion for ${item.part_number}`} /></td>
                    <td className="max-w-[240px] border-b border-line-2 px-3 py-2">
                      <span className="block font-mono text-[12.5px] text-ink">{item.part_number}</span>
                      <span className="block truncate text-[11.5px] text-ink-3" title={item.description}>{item.description || 'No description'}</span>
                    </td>
                    <td className="border-b border-line-2 px-3 py-2">{cell('brand', item.current?.brand)}</td>
                    <td className="border-b border-line-2 px-3 py-2">{cell('category', item.current?.category)}</td>
                    <td className="border-b border-line-2 px-3 py-2 text-right tabular-nums">{item.suggest.lead_time_days ? <span className="font-semibold text-status-violet-ink">{formatNumber(item.suggest.lead_time_days)} d</span> : <span className="text-ink-3">—</span>}</td>
                    <td className="max-w-[260px] border-b border-line-2 px-3 py-2 text-[11.5px] text-ink-3">{Object.values(item.reasons || {}).join(' · ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="mr-auto text-[12.5px] text-ink-3">{plural(checked.size, 'item')} selected of {formatNumber(items.length)}</span>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" variant="outline" className="text-ink" disabled={busy || !checked.size} onClick={() => apply(items.filter((item) => checked.has(item.id)))} data-testid="suggestions-accept-selected">
            Accept selected ({formatNumber(checked.size)})
          </Button>
          <Button type="button" disabled={busy || !items.length} onClick={() => apply(items)} data-testid="suggestions-accept-all">
            {busy ? 'Saving…' : `Accept all ${formatNumber(items.length)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
